import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type QuickSearchOpenOptions, useQuickSearchBarEvents} from '@github-ui/quick-search/bar-events'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {getPrimaryKeybinding} from '@github-ui/ui-commands'
import {useMediaQuery} from '@github-ui/use-media-query'
import {CommandPaletteIcon, SearchIcon} from '@primer/octicons-react'
import {ButtonGroup} from '@primer/react'
import {clsx} from 'clsx'
import {lazy, startTransition, Suspense, useCallback, useEffect, useRef, useState, useSyncExternalStore} from 'react'

import {NavButtonItem} from '../../primitives/NavItemLink'
import {shouldHideTopNav, useTopNavConfig, useTopNavSearchState} from '../../primitives/TopNav'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import styles from './Search.module.css'

const QuickSearch = lazy(async () => {
  const module = await import('@github-ui/quick-search')
  return {default: module.QuickSearch}
})

const FeedbackDialog = lazy(async () => {
  const module = await import('@github-ui/blackbird-dialogs/FeedbackDialog')
  return {default: module.FeedbackDialog}
})

const loadSavedSearchesDialog = () => import('@github-ui/blackbird-dialogs/SavedSearchesDialog')
const SavedSearchesDialog = lazy(async () => {
  const module = await loadSavedSearchesDialog()
  return {default: module.SavedSearchesDialog}
})

async function preloadQuickSearch() {
  const module = await import('@github-ui/quick-search/preload')
  module.preloadQuickSearch()
}

// Mirrors the breakpoint in Search.module.css that toggles between the wide
// button group and the compact icon button.
const COMPACT_VIEWPORT_QUERY = '(max-width: 1024px)'

export type SearchProps = {
  show?: boolean
  showCommandPalette?: boolean
  isSearchPage?: boolean
  isJumpToSearch?: boolean
  searchContext?: Record<string, string>
  locationSearch?: string
}

export function Search({
  show,
  isJumpToSearch,
  showCommandPalette,
  isSearchPage,
  searchContext,
  locationSearch: initialLocationSearch,
}: SearchProps) {
  const locationSearch = useLocationSearch(initialLocationSearch)
  const {sendItemClick} = useGlobalNavAnalytics('QUICK_SEARCH')
  const {quickSearchOpen, topNavHidden} = useTopNavSearchState()
  const {setQuickSearchOpen, setTopNavHidden, loadCopilotChat} = useTopNavConfig()
  const searchKeybinding = getPrimaryKeybinding('global-navigation:search') || '/'
  const quickSearchAriaLabel = `Open quick search dialog, type ${searchKeybinding} to search`
  // The quick-search panel anchors (top-flush, matching left/width) to the header
  // search region, which is a parent element, so we resolve it from the DOM.
  const quickSearchAnchorRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    quickSearchAnchorRef.current = document.querySelector<HTMLElement>('[data-testid="top-nav-center"]')
  }, [])

  // The header renders two search triggers (the regular button group and a
  // compact icon button) that are toggled by CSS at 1024px (see
  // Search.module.css). When the overlay closes, return focus to whichever
  // trigger the current viewport actually shows so focus lands back on the
  // button the user sees. This matters most for keyboard opens: the command
  // handler calls `.click()` on the compact button, which does not move focus
  // to it, so Primer's fallback (restore previous focus) would otherwise drop
  // focus to <body> — and on wide screens the compact button is hidden anyway.
  const largeSearchButtonRef = useRef<HTMLButtonElement>(null)
  const smallSearchButtonRef = useRef<HTMLButtonElement>(null)
  const quickSearchReturnFocusRef = useRef<HTMLElement>(null)
  const isCompactViewport = useMediaQuery(COMPACT_VIEWPORT_QUERY)

  useEffect(() => {
    quickSearchReturnFocusRef.current = isCompactViewport ? smallSearchButtonRef.current : largeSearchButtonRef.current
  }, [isCompactViewport])

  const searchPageQuery = isSearchPage ? (new URLSearchParams(locationSearch).get('q')?.trim() ?? '') : ''

  // Reflects edits made inside the QuickSearch dialog. It persists after the
  // dialog closes; `null` means "not edited", so we fall back to the query
  // derived from the URL.
  const [editedQuery, setEditedQuery] = useState<string | null>(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [savedSearchesDialogQuery, setSavedSearchesDialogQuery] = useState<string | null>(null)
  const [retainQuickSearchScrollPosition, setRetainQuickSearchScrollPosition] = useState(false)
  // What QuickSearch reopens with — the entered text is kept on every page.
  const seededQuery = editedQuery ?? searchPageQuery
  // What the header button shows — only surfaced on the search page; elsewhere
  // the button keeps its placeholder.
  const displayedQuery = isSearchPage ? seededQuery : ''
  const lazyLoadSuggestions = isFeatureEnabled('quick_search_lazy_suggestions')
  // Preserve eager preloading until intent-based suggestion loading is enabled.
  useEffect(() => {
    if (!lazyLoadSuggestions) void preloadQuickSearch()
  }, [lazyLoadSuggestions])

  const handleQuickSearchIntent = useCallback(() => {
    loadCopilotChat()
    void preloadQuickSearch()
    void loadSavedSearchesDialog()
  }, [loadCopilotChat])

  const showQuickSearch = useCallback(() => {
    void preloadQuickSearch()
    void loadSavedSearchesDialog()
    startTransition(() => {
      setQuickSearchOpen(true)
    })
  }, [setQuickSearchOpen])

  const openQuickSearch = useCallback(() => {
    setRetainQuickSearchScrollPosition(false)
    quickSearchReturnFocusRef.current = isCompactViewport ? smallSearchButtonRef.current : largeSearchButtonRef.current
    showQuickSearch()
  }, [isCompactViewport, showQuickSearch])

  const openQuickSearchFromEvent = useCallback(
    (options?: QuickSearchOpenOptions) => {
      setRetainQuickSearchScrollPosition(options?.retainScrollPosition ?? false)
      quickSearchReturnFocusRef.current =
        options?.returnTarget ?? (isCompactViewport ? smallSearchButtonRef.current : largeSearchButtonRef.current)
      showQuickSearch()
    },
    [isCompactViewport, showQuickSearch],
  )

  const closeQuickSearch = useCallback(() => {
    // Close synchronously (not in a transition). Deferring the close leaves the
    // overlay painted while React prepares the closed tree, and an urgent
    // re-render committed in that window can briefly reveal the underlying
    // "Search or jump to…" button before ours repaints with the correct query.
    setQuickSearchOpen(false)
  }, [setQuickSearchOpen])

  const handleFeedbackClose = useCallback(() => {
    setFeedbackOpen(false)
    quickSearchReturnFocusRef.current?.focus()
  }, [])

  const handleManageSavedSearches = useCallback(
    (query: string) => {
      setQuickSearchOpen(false)
      setSavedSearchesDialogQuery(query)
    },
    [setQuickSearchOpen],
  )

  const handleSavedSearchesClose = useCallback(() => {
    setSavedSearchesDialogQuery(null)
  }, [])

  const handleQuickSearchSubmit = useCallback(
    (_query: string) => {
      sendItemClick('SEARCH_SUBMISSION')
    },
    [sendItemClick],
  )

  const handleQuickSearchSuggestionSelect = useCallback(
    (type: 'jump-to' | 'search' | 'autocomplete' | 'manage-saved-searches') => {
      sendItemClick('SUGGESTION_SELECTION', type)
    },
    [sendItemClick],
  )

  const {navigateToSearch} = useQuickSearchBarEvents({
    enabled: true,
    isSearchPage,
    searchPageQuery,
    onSetQuery: setEditedQuery,
    onRequestOpen: openQuickSearchFromEvent,
    onRequestFeedback: () => {
      setQuickSearchOpen(false)
      setFeedbackOpen(true)
    },
  })

  useEffect(() => {
    const urlParams = new URLSearchParams(locationSearch)

    const hideTopNav = shouldHideTopNav(urlParams, isSearchPage ?? false, isJumpToSearch ?? false)

    if (topNavHidden !== hideTopNav) {
      setTopNavHidden(hideTopNav)
    }
  }, [locationSearch, setTopNavHidden, topNavHidden, isSearchPage, isJumpToSearch])

  if (!show) {
    return null
  }

  return (
    <>
      <ButtonGroup className={clsx(styles.searchButtonGroup, isSearchPage && styles.searchPage)}>
        <NavButtonItem
          ref={largeSearchButtonRef}
          className={styles.searchButton}
          onClick={openQuickSearch}
          onFocus={handleQuickSearchIntent}
          onMouseEnter={handleQuickSearchIntent}
          icon={SearchIcon}
          analyticsKey="SEARCH"
          aria-label={quickSearchAriaLabel}
          aria-haspopup="dialog"
          aria-expanded={quickSearchOpen}
          linkText={
            displayedQuery ? (
              <span className={clsx(styles.placeholder, styles.text, styles.value)}>{displayedQuery}</span>
            ) : (
              <span className={clsx(styles.placeholder, styles.text, styles.value)}>
                Type <kbd className={styles.kbd}>{searchKeybinding}</kbd> to search
              </span>
            )
          }
        />
        {showCommandPalette && (
          <NavButtonItem
            className="js-activate-command-palette"
            icon={CommandPaletteIcon}
            aria-label="Command palette"
            analyticsKey="COMMAND_PALETTE"
          />
        )}
      </ButtonGroup>
      <NavButtonItem
        ref={smallSearchButtonRef}
        icon={SearchIcon}
        aria-label={quickSearchAriaLabel}
        commandId="global-navigation:search"
        onClick={openQuickSearch}
        onFocus={handleQuickSearchIntent}
        onMouseEnter={handleQuickSearchIntent}
        className={styles.smallSearchButton}
        analyticsKey="SEARCH"
      />
      {quickSearchOpen && (
        <Suspense fallback={null}>
          <QuickSearch
            isOpen={quickSearchOpen}
            anchorRef={quickSearchAnchorRef}
            retainScrollPosition={retainQuickSearchScrollPosition}
            initialQuery={seededQuery}
            returnFocusRef={quickSearchReturnFocusRef}
            onClose={closeQuickSearch}
            onSearchSubmit={handleQuickSearchSubmit}
            onSuggestionSelect={handleQuickSearchSuggestionSelect}
            onFeedbackClick={() => {
              setQuickSearchOpen(false)
              setFeedbackOpen(true)
            }}
            onQueryChange={setEditedQuery}
            onManageSavedSearches={handleManageSavedSearches}
            onNavigateToSearch={navigateToSearch}
            searchContext={searchContext}
            isSearchPage={isSearchPage}
          />
        </Suspense>
      )}
      {feedbackOpen && (
        <Suspense fallback={null}>
          <FeedbackDialog onClose={handleFeedbackClose} />
        </Suspense>
      )}
      {savedSearchesDialogQuery !== null && (
        <Suspense fallback={null}>
          <SavedSearchesDialog
            initialQuery={savedSearchesDialogQuery}
            initialView="manage-if-has-scopes"
            onClose={handleSavedSearchesClose}
            returnFocusRef={quickSearchReturnFocusRef}
          />
        </Suspense>
      )}
    </>
  )
}

function subscribeToLocationSearch(onStoreChange: () => void) {
  window.addEventListener('popstate', onStoreChange)
  return () => {
    window.removeEventListener('popstate', onStoreChange)
  }
}

function getLocationSearchSnapshot() {
  return window.location.search
}

function useLocationSearch(initialLocationSearch?: string) {
  return useSyncExternalStore(
    subscribeToLocationSearch,
    getLocationSearchSnapshot,
    () => initialLocationSearch ?? ssrSafeLocation.search ?? '',
  )
}
